import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Quote, Tag, Printer, Copy, Check, ArrowLeft, Pencil, ShieldCheck, Trash2, Save, X, Loader2, Mail } from "lucide-react";
import { ARCHETYPES, CONSENT_OPTIONS, INDUSTRIES, COMPANY_SIZES, TRUE_COLORS_SOLUTIONS, CHALLENGE_CATEGORIES, PROOF_POINTS } from "../lib/constants";
import { toast } from "sonner";

const EDITABLE_FIELDS = [
  "title", "company_name", "industry", "company_size",
  "challenge_text", "challenge_categories",
  "trigger_to_act", "true_colors_solution",
  "after_text", "proof_points", "client_quote", "consent_status",
  "people_trained", "training_date", "delivery_format", "engagement_type",
  "client_contact_name", "client_contact_email",
  "tags",
];

export default function StoryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(undefined);
  const [shareCopied, setShareCopied] = useState(false);

  // Inline edit state — flipping `editing` to true reveals controls in place
  // of the read-only fields. `draft` holds the working copy until Save.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingCeo, setSendingCeo] = useState(false);

  useEffect(() => {
    api.get(`/stories/${id}`)
      .then(({ data }) => setS(data))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) setS({ __error: "Forbidden", __status: 403 });
        else if (status === 404) setS({ __error: "Not found", __status: 404 });
        else setS({ __error: err?.message || "Couldn't load this story", __status: status || 0 });
      });
  }, [id]);

  const isMine = s?.author_id === user?.id;
  const isSuperAdmin = user?.role === "super_admin";
  const canEdit = !!s && !s.__error && (isMine || isSuperAdmin);

  const startEdit = () => {
    setDraft({
      title: s.title || "",
      company_name: s.company_name || "",
      industry: s.industry || "",
      company_size: s.company_size || "",
      challenge_text: s.challenge_text || "",
      challenge_categories: Array.isArray(s.challenge_categories) ? [...s.challenge_categories] : [],
      trigger_to_act: s.trigger_to_act || "",
      true_colors_solution: Array.isArray(s.true_colors_solution) ? [...s.true_colors_solution] : [],
      after_text: s.after_text || "",
      proof_points: Array.isArray(s.proof_points) ? [...s.proof_points] : [],
      client_quote: s.client_quote || "",
      consent_status: s.consent_status || "",
      people_trained: s.people_trained || "",
      training_date: s.training_date || "",
      delivery_format: s.delivery_format || "",
      engagement_type: s.engagement_type || "",
      client_contact_name: s.client_contact_name || "",
      client_contact_email: s.client_contact_email || "",
      tags: { ...(s.tags || {}) },
    });
    setEditing(true);
  };
  const cancelEdit = () => { setEditing(false); setDraft(null); };

  const saveEdit = async () => {
    if (!draft.title?.trim() && !draft.challenge_text?.trim()) {
      toast.error("Add a title or challenge before saving.");
      return;
    }
    if (!draft.company_name?.trim()) {
      toast.error("Company name is required.");
      return;
    }
    setSaving(true);
    const payload = { ...draft };
    if (!payload.consent_status) delete payload.consent_status;
    // Mirror outcome to tags.challenge_type / company_size for filter consistency
    payload.tags = {
      ...(payload.tags || {}),
      industry: payload.industry || undefined,
      company_size: payload.company_size || undefined,
      challenge_type: (payload.challenge_categories?.[0]) || payload.tags?.challenge_type || undefined,
    };
    try {
      const { data: updated } = await api.put(`/stories/${id}`, payload);
      setS(updated);
      setEditing(false);
      setDraft(null);
      toast.success("Story updated.");
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setSaving(false);
    }
  };

  if (s === undefined) return <div className="p-10 text-slate-400 text-center" data-testid="story-loading">Loading…</div>;
  if (s && s.__error) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 text-center">
        <div className="tc-card p-10">
          <div className="font-display text-3xl text-tc-ink">
            {s.__status === 403 ? "Not yours to view" : s.__status === 404 ? "Story not found" : "Something went sideways"}
          </div>
          <p className="text-slate-500 mt-2">
            {s.__status === 403 ? "This story is still in review and can only be viewed by its author or a super admin." : s.__status === 404 ? "It may have been deleted or you have an old link." : "Refresh the page or head back to the library."}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button onClick={() => window.location.reload()} className="h-11 px-4 rounded-xl bg-white border border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50">
              Refresh
            </button>
            <button onClick={() => navigate("/library")} className="h-11 px-5 rounded-xl text-white font-bold text-sm" style={{ background: "#f68b1f" }}>
              Back to Library
            </button>
          </div>
        </div>
      </div>
    );
  }

  const archetype = ARCHETYPES[user?.archetype || "blue"];
  const consent = CONSENT_OPTIONS.find((o) => o.value === s.consent_status)?.label || ",";
  const mo = s.measurable_outcomes || {};

  const copyShare = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 1500);
  };

  const deleteStory = async () => {
    const subjectLabel = s.source === "client"
      ? `this testimonial from ${s.client_submitter_name || "the client"}`
      : "this story";
    if (!window.confirm(`Delete ${subjectLabel}? This can't be undone.`)) return;
    try {
      await api.delete(`/stories/${s.id}`);
      toast.success("Story deleted.");
      navigate("/library");
    } catch (err) {
      toast.error("Delete failed", { description: err?.response?.data?.detail || err?.message });
    }
  };

  const sendCeoThanks = async () => {
    if (!window.confirm("Send Robert a thank-you draft email for this story?")) return;
    setSendingCeo(true);
    try {
      const { data } = await api.post(`/admin/stories/${s.id}/ceo-thanks`);
      if (data?.result?.skipped) {
        const reason = data.result.reason;
        if (reason === "no_client_contact") {
          toast.error("No client contact on file", { description: "Add a client contact name or email first." });
        } else if (reason === "email_disabled") {
          toast.success("Draft generated (emails are off in preview).");
        } else {
          toast.success("Draft sent.");
        }
      } else {
        toast.success("Robert got the draft. You're CC'd.");
      }
    } catch (err) {
      toast.error("Couldn't send", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setSendingCeo(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <div className="flex items-center justify-between mb-4 no-print">
        <button onClick={() => navigate(-1)} data-testid="detail-back" className="text-slate-600 font-bold flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && !editing && (
            <button onClick={startEdit} data-testid="detail-edit" className="h-10 px-3 rounded-xl border border-slate-200 font-bold text-sm flex items-center gap-1 hover:bg-slate-100">
              <Pencil size={14} /> Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={cancelEdit} data-testid="detail-cancel" className="h-10 px-3 rounded-xl border border-slate-200 font-bold text-sm flex items-center gap-1 hover:bg-slate-100">
                <X size={14} /> Cancel
              </button>
              <button onClick={saveEdit} disabled={saving} data-testid="detail-save" className="h-10 px-4 rounded-xl bg-green-600 text-white font-bold text-sm flex items-center gap-1 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
          {!editing && (
            <>
              <button onClick={copyShare} data-testid="detail-share" className="h-10 px-3 rounded-xl border border-slate-200 font-bold text-sm flex items-center gap-1 hover:bg-slate-100">
                {shareCopied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />} {shareCopied ? "Copied" : "Share link"}
              </button>
              <button onClick={() => window.print()} data-testid="detail-print" className="h-10 px-3 rounded-xl text-white font-bold text-sm flex items-center gap-1" style={{ background: "#083050" }}>
                <Printer size={14} /> Print / PDF
              </button>
              {isSuperAdmin && s.status === "approved" && (
                <button
                  onClick={sendCeoThanks}
                  disabled={sendingCeo}
                  data-testid="detail-ceo-thanks"
                  title="Send Robert a thank-you draft for this client"
                  className="h-10 px-3 rounded-xl text-white font-bold text-sm flex items-center gap-1 disabled:opacity-50"
                  style={{ background: "#f68b1f" }}
                >
                  {sendingCeo ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {sendingCeo ? "Sending…" : "CEO thank-you"}
                </button>
              )}
              {isSuperAdmin && (
                <button onClick={deleteStory} data-testid="detail-delete" title="Delete this story" className="h-10 px-3 rounded-xl border border-red-200 text-red-600 font-bold text-sm flex items-center gap-1 hover:bg-red-50">
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="tc-card p-7 sm:p-10 print-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="tc-pill bg-slate-100 text-slate-700"><Tag size={12} /> {s.industry || ","} · {s.company_size || ","}</div>
          {s.status === "approved" && (
            <span className="tc-pill bg-green-50 text-green-700"><ShieldCheck size={12} /> Approved {s.approved_at?.slice(0, 10)}</span>
          )}
          {s.status === "pending" && <span className="tc-pill bg-amber-50 text-amber-700">In review</span>}
          {s.status === "draft" && <span className="tc-pill bg-slate-100 text-slate-700">Draft</span>}
          {s.status === "rejected" && <span className="tc-pill bg-red-50 text-red-700">Needs edits</span>}
        </div>

        {/* Company name */}
        {!editing && (s.company_name || s.client_submitter_org) && (
          <div className="text-xs uppercase tracking-widest font-extrabold text-orange-600 mt-3" data-testid="detail-company">
            {s.company_name || s.client_submitter_org}
          </div>
        )}
        {editing && (
          <div className="mt-4">
            <Label text="Company (required, internal)" />
            <input
              value={draft.company_name}
              onChange={(e) => setDraft({ ...draft, company_name: e.target.value })}
              placeholder="e.g. Cleveland Clinic"
              data-testid="detail-edit-company"
              className={INPUT_CLS}
            />
          </div>
        )}

        {/* Title — short headline only. We deliberately don't fall back to
            challenge_text here: rendering the whole paragraph as a giant
            headline (it happened for untitled stories) looked broken. */}
        {!editing ? (
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink mt-3 leading-tight" data-testid="detail-title">
            {s.title || (s.company_name || s.client_submitter_org || "Untitled story")}
          </h1>
        ) : (
          <div className="mt-4">
            <Label text="Story title" />
            <input
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={80}
              placeholder="e.g. Healthcare team rebuilds after merger"
              data-testid="detail-edit-title"
              className={`${INPUT_CLS} text-xl font-display`}
            />
          </div>
        )}

        {/* Industry + size (edit only) */}
        {editing && (
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <Label text="Industry" />
              <select value={draft.industry} onChange={(e) => setDraft({ ...draft, industry: e.target.value })} data-testid="detail-edit-industry" className={INPUT_CLS}>
                <option value="">—</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <Label text="Company size" />
              <select value={draft.company_size} onChange={(e) => setDraft({ ...draft, company_size: e.target.value })} data-testid="detail-edit-size" className={INPUT_CLS}>
                <option value="">—</option>
                {COMPANY_SIZES.map((sz) => <option key={sz} value={sz}>{sz}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Challenge categories */}
        {!editing && s.challenge_categories?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {s.challenge_categories.map((c) => <span key={c} className="tc-pill bg-orange-50 text-orange-800">{c}</span>)}
          </div>
        )}
        {editing && (
          <div className="mt-4">
            <Label text="Challenge tags" />
            <ChipsPicker
              options={CHALLENGE_CATEGORIES}
              value={draft.challenge_categories}
              onChange={(arr) => setDraft({ ...draft, challenge_categories: arr })}
              testid="detail-edit-challenge-cats"
            />
          </div>
        )}

        {/* Challenge body */}
        {!editing ? (
          s.title && s.challenge_text && (
            <div className="mt-5" data-testid="detail-challenge">
              <div className="tc-eyebrow text-slate-400">Challenge</div>
              <p className="text-slate-800 leading-relaxed mt-1">{s.challenge_text}</p>
            </div>
          )
        ) : (
          <EditableSection
            label="Challenge"
            value={draft.challenge_text}
            onChange={(v) => setDraft({ ...draft, challenge_text: v })}
            testid="detail-edit-challenge"
          />
        )}

        {/* Trigger */}
        {!editing ? (
          <Section label="Trigger to act" body={s.trigger_to_act} />
        ) : (
          <EditableSection
            label="Trigger to act"
            value={draft.trigger_to_act}
            onChange={(v) => setDraft({ ...draft, trigger_to_act: v })}
            testid="detail-edit-trigger"
          />
        )}

        {/* TC solutions */}
        {!editing ? (
          <Section label="True Colors solutions" body={Array.isArray(s.true_colors_solution) ? s.true_colors_solution.join(", ") : s.true_colors_solution} />
        ) : (
          <div className="mt-5">
            <Label text="True Colors solutions" />
            <ChipsPicker
              options={TRUE_COLORS_SOLUTIONS}
              value={draft.true_colors_solution}
              onChange={(arr) => setDraft({ ...draft, true_colors_solution: arr })}
              testid="detail-edit-solutions"
            />
          </div>
        )}

        {/* Engagement details — read-only stat-row when not editing,
            4-input grid when editing. Quietly hides if nothing's filled in. */}
        {!editing ? (
          <EngagementFacts story={s} />
        ) : (
          <div className="mt-5">
            <Label text="Engagement details (optional)" />
            <p className="text-xs text-slate-500 mb-2">These flesh out the story and feed Robert's CEO thank-you email.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <LabeledInput
                placeholder="e.g. ~50 across 2 cohorts"
                label="People trained"
                value={draft.people_trained}
                onChange={(v) => setDraft({ ...draft, people_trained: v })}
                testid="detail-edit-people-trained"
              />
              <LabeledInput
                placeholder="e.g. March 2025"
                label="Training date"
                value={draft.training_date}
                onChange={(v) => setDraft({ ...draft, training_date: v })}
                testid="detail-edit-training-date"
              />
              <LabeledInput
                placeholder="e.g. 2-day Train-the-Trainer"
                label="Engagement type"
                value={draft.engagement_type}
                onChange={(v) => setDraft({ ...draft, engagement_type: v })}
                testid="detail-edit-engagement-type"
              />
              <div>
                <Label text="Format" />
                <div className="grid grid-cols-3 gap-1.5 mt-1">
                  {["In-person", "Virtual", "Hybrid"].map((fmt) => (
                    <button
                      key={fmt}
                      type="button"
                      onClick={() => setDraft({ ...draft, delivery_format: draft.delivery_format === fmt ? "" : fmt })}
                      data-testid={`detail-edit-format-${fmt.toLowerCase()}`}
                      className={`h-10 rounded-lg border-2 text-xs font-bold transition-all ${draft.delivery_format === fmt ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>
              <LabeledInput
                label="Client contact name"
                placeholder="Who Robert thanks"
                value={draft.client_contact_name}
                onChange={(v) => setDraft({ ...draft, client_contact_name: v })}
                testid="detail-edit-client-contact-name"
              />
              <LabeledInput
                label="Client contact email"
                placeholder="Where Robert sends the thank-you"
                value={draft.client_contact_email}
                onChange={(v) => setDraft({ ...draft, client_contact_email: v })}
                testid="detail-edit-client-contact-email"
              />
            </div>
          </div>
        )}

        {/* After */}
        {!editing ? (
          <Section label="What changed" body={s.after_text} />
        ) : (
          <EditableSection
            label="What changed"
            value={draft.after_text}
            onChange={(v) => setDraft({ ...draft, after_text: v })}
            testid="detail-edit-after"
          />
        )}

        {(mo.turnover_rate || mo.engagement_score || mo.conflict_incidents || mo.productivity_data || (mo.custom_label && mo.custom_value)) && !editing && (
          <div className="mt-6">
            <div className="tc-eyebrow text-slate-400 mb-2">Measurable outcomes</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {mo.turnover_rate && <Metric label="Turnover" value={mo.turnover_rate} color="#f68b1f" />}
              {mo.engagement_score && <Metric label="Engagement" value={mo.engagement_score} color="#0091be" />}
              {mo.conflict_incidents && <Metric label="Conflict" value={mo.conflict_incidents} color="#ffd400" />}
              {mo.productivity_data && <Metric label="Productivity" value={mo.productivity_data} color="#7ac143" />}
              {mo.custom_label && mo.custom_value && <Metric label={mo.custom_label} value={mo.custom_value} color={archetype.color} />}
            </div>
          </div>
        )}

        {/* Proof points */}
        {!editing ? (
          s.proof_points && s.proof_points.length > 0 && (
            <div className="mt-6" data-testid="detail-proof-points">
              <div className="tc-eyebrow text-slate-400 mb-2">Proof points</div>
              <div className="flex flex-wrap gap-1.5">
                {s.proof_points.map((p) => (
                  <span key={p} className="tc-pill bg-yellow-50 text-yellow-900 border border-yellow-200">
                    <Check size={12} /> {p}
                  </span>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="mt-5">
            <Label text="Proof points" />
            <ChipsPicker
              options={PROOF_POINTS}
              value={draft.proof_points}
              onChange={(arr) => setDraft({ ...draft, proof_points: arr })}
              testid="detail-edit-proof"
            />
            <p className="text-[11px] text-slate-400 mt-1">Pick from the standard categories or add custom outcomes via the full editor.</p>
          </div>
        )}

        {/* Client quote */}
        {!editing ? (
          s.client_quote && (
            <blockquote className="mt-6 border-l-4 pl-5 italic text-slate-700 text-lg leading-relaxed" style={{ borderColor: archetype.color }}>
              <Quote size={20} className="inline -mt-1 mr-1" style={{ color: archetype.color }} />
              {s.client_quote}
            </blockquote>
          )
        ) : (
          <EditableSection
            label="Client quote"
            value={draft.client_quote}
            onChange={(v) => setDraft({ ...draft, client_quote: v })}
            placeholder="Any standout pull-quote from the client?"
            testid="detail-edit-quote"
          />
        )}

        {/* Consent (edit only — read-only is shown in the footer) */}
        {editing && (
          <div className="mt-5">
            <Label text="Consent" />
            <select value={draft.consent_status} onChange={(e) => setDraft({ ...draft, consent_status: e.target.value })} data-testid="detail-edit-consent" className={INPUT_CLS}>
              <option value="">—</option>
              {CONSENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}

        {/* Media — view only */}
        {!editing && s.media?.length > 0 && (
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {s.media.map((m, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100" data-testid={`detail-media-${i}`}>
                {m.kind === "image" ? (
                  <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <video controls className="w-full h-full" src={fileUrl(m.path)} />
                )}
              </div>
            ))}
          </div>
        )}

        {!editing && (
          <div className="mt-8 pt-5 border-t border-slate-100 flex flex-wrap gap-4 text-sm text-slate-500">
            <div><span className="font-bold text-slate-700">Captured by:</span> {s.author_name || ","}</div>
            <div><span className="font-bold text-slate-700">Consent:</span> {consent}</div>
            {s.tags?.outcome_category && <div><span className="font-bold text-slate-700">Outcome:</span> {s.tags.outcome_category}</div>}
            {s.client_id && <div><span className="font-bold text-slate-700">Client ID:</span> {s.client_id}</div>}
          </div>
        )}

        {s.admin_notes && !editing && (
          <div className="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
            <div className="font-bold mb-1">Admin notes</div>
            {s.admin_notes}
          </div>
        )}
      </div>
    </div>
  );
}

const INPUT_CLS = "w-full h-11 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm bg-white";
const TEXTAREA_CLS = "w-full min-h-[110px] rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 py-2 text-sm bg-white";

const Label = ({ text }) => <div className="tc-eyebrow text-slate-500 mb-1.5">{text}</div>;

const Section = ({ label, body }) => body ? (
  <div className="mt-5">
    <div className="tc-eyebrow text-slate-400">{label}</div>
    <p className="text-slate-800 leading-relaxed mt-1 whitespace-pre-wrap">{body}</p>
  </div>
) : null;

const EditableSection = ({ label, value, onChange, placeholder, testid }) => (
  <div className="mt-5">
    <Label text={label} />
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || ""}
      data-testid={testid}
      className={TEXTAREA_CLS}
    />
  </div>
);

const LabeledInput = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <Label text={label} />
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || ""}
      data-testid={testid}
      className={INPUT_CLS}
    />
  </div>
);

function EngagementFacts({ story }) {
  // Compact stat-row showing whichever engagement details are populated.
  // Quietly hides if none are set so old stories don't render an empty box.
  const items = [
    ["Engagement", story.engagement_type],
    ["Format", story.delivery_format],
    ["When", story.training_date],
    ["People trained", story.people_trained],
  ].filter(([, v]) => v && String(v).trim());
  // Client contact (rep-told stories OR fallback). Authenticated team only.
  const contactName = story.client_contact_name || story.client_submitter_name;
  const contactEmail = story.client_contact_email || story.client_submitter_email;
  const hasContact = contactName || contactEmail;
  if (items.length === 0 && !hasContact) return null;
  return (
    <div className="mt-5 space-y-3" data-testid="engagement-facts">
      {items.length > 0 && (
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map(([label, value]) => (
            <div key={label}>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
              <div className="text-sm font-bold text-tc-ink mt-0.5">{value}</div>
            </div>
          ))}
        </div>
      )}
      {hasContact && (
        <div className="rounded-2xl bg-orange-50/40 border border-orange-200 p-4 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid="client-contact-row">
          <div className="text-[10px] uppercase tracking-widest font-bold text-orange-700">Client contact</div>
          {contactName && <div className="text-sm font-bold text-tc-ink">{contactName}</div>}
          {contactEmail && (
            <a href={`mailto:${contactEmail}`} className="text-sm text-[#0091be] font-bold hover:underline">{contactEmail}</a>
          )}
        </div>
      )}
    </div>
  );
}

function ChipsPicker({ options, value, onChange, testid }) {
  const set = useMemo(() => new Set(value || []), [value]);
  const toggle = (o) => {
    const next = new Set(set);
    if (next.has(o)) next.delete(o); else next.add(o);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const picked = set.has(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            className={`tc-pill border transition-colors ${picked ? "bg-tc-ink text-white border-tc-ink" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

const Metric = ({ label, value, color }) => (
  <div className="rounded-2xl p-4 border border-slate-200">
    <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
    <div className="font-display text-2xl mt-0.5" style={{ color }}>{value}</div>
  </div>
);
